import {
  ErrorBoundary,
  Loading,
  LookingForSomethingElse,
  SubscriptionType,
  publishEvent,
  useRefetch
} from '@uma/react-commons/dist/index';
import { IconArrowLeft, IconArrowRight, useDirection } from '@veneer/core';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FeatureFlagProps } from 'src/App';
import { Content, DrillDownHeader } from 'src/App/styles';
import HaveQuestions from 'src/components/HaveQuestions';
import { MoreOptions } from 'src/components/MoreOptions';
import { Payment } from 'src/components/Payment';
import PlanDetails from 'src/components/PlanDetails';
import { Shipping } from 'src/components/Shipping';
import { SubscriptionWarning } from 'src/components/SubscriptionWarning';
import { NotificationType } from 'src/components/SubscriptionWarning/types';
import { SubscriptionsProps } from 'src/components/SubscriptionsCard/types';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import GET_SUBSCRIPTIONS_DETAILS from 'src/graphql/getSubscriptionDetails';
import getSubscriptionWarningState from 'src/helpers/getSubscriptionWarningState';
import { Section } from 'src/styles/styles';
import {
  generateAnalyticsOfScreenDisplayedByType,
  getModuleDisplayedSubscriptionsDetailsError,
  screenNameSubscriptionDetails,
  screenPathSubscriptionDetails
} from 'src/utils/analytics';
import { SubscriptionsWarningState } from 'src/utils/enums';
import { formatFullDate } from 'src/utils/masks';

type SubscriptionParamType = keyof typeof SubscriptionType;

const SubscriptionDetails = (featureFlagProps: FeatureFlagProps) => {
  const { translate, northboundAPIs } = useDependencyManagerContext();

  const operation = 'GET_SUBSCRIPTIONS_DETAILS';

  const queryParams = new URLSearchParams(location.search);

  const navigation = northboundAPIs?.v1?.navigation;

  const path = navigation.location.pathname.split('/').pop();

  const resourceId = path.includes('?') ? path.split('?')[0] : path;

  const type = queryParams.get('type') as unknown as SubscriptionParamType;

  const { isRTL } = useDirection();

  const ArrowIcon = isRTL ? IconArrowRight : IconArrowLeft;

  const useQuery = northboundAPIs?.v1?.graphql.reactTools.useQuery;

  const { loading, error, data, refetch } = useQuery(
    GET_SUBSCRIPTIONS_DETAILS,
    {
      variables: {
        subscriptionFilter: {
          resourceId: resourceId,
          subscriptionType: type
        }
      }
    }
  );

  const { isRefetching, handleRefetch } = useRefetch({
    refetch,
    error,
    operation
  });

  const country = northboundAPIs?.v1?.localization.country;
  const language = northboundAPIs?.v1?.localization.language;

  const {
    originalFeedbackExperience,
    isLoading: isOriginalFeedbackExperienceLoading
  } = featureFlagProps;

  const memoizedOriginalFeedbackExperience = useMemo(
    () => originalFeedbackExperience,
    [originalFeedbackExperience]
  );

  const memoizedIsOriginalFeedbackExperienceLoading = useMemo(
    () => isOriginalFeedbackExperienceLoading,
    [isOriginalFeedbackExperienceLoading]
  );

  const showOriginalFeedbackExperience =
    memoizedOriginalFeedbackExperience &&
    !memoizedIsOriginalFeedbackExperienceLoading;

  const subscription: SubscriptionsProps = data?.account?.subscriptions[0];
  const subscriptionName =
    subscription?.type === SubscriptionType.HP_ALL_IN
      ? translate('common.allInPrintPlan', 'HP All-In Plan')
      : translate('common.instantInk', 'HP Instant Ink');

  const showSubscriptionWarning = useCallback(() => {
    const warningState = getSubscriptionWarningState(
      subscription?.state,
      subscription?.type
    );

    function renderSubscriptionWarningPending() {
      const htmlString = translate(
        'subscriptions.warningInactiveMessagePending',
        {
          defaultValue:
            'Your {{subscriptionName}} is almost ready! Click {{tagReplaceStart}}Next steps {{tagReplaceEnd}}to see updates and any actions you might need to take.',
          replace: { subscriptionName }
        }
      )
        .replace('{{tagReplaceStart}}', '<strong>')
        .replace('{{tagReplaceEnd}}', '</strong>');

      return (
        <SubscriptionWarning
          data={{
            type: 'informative' as NotificationType,
            label: translate('subscriptions.nextSteps', 'Next steps'),
            subscriptionType: subscription?.type,
            subscriptionState: SubscriptionsWarningState.PENDING,
            title: (
              <span
                data-testid="subscription-status-pending"
                dangerouslySetInnerHTML={{ __html: htmlString }}
              ></span>
            )
          }}
        />
      );
    }

    function renderSubscriptionWarningExpiring() {
      const chargeDate = formatFullDate(
        subscription?.billing?.nextBillingCycle?.chargeDate
      );

      const htmlString = translate(
        'subscriptions.warningInactiveMessageExpiring',
        {
          defaultValue:
            'Heads up! Your subscription and services will end on {{chargeDate}}. Click {{tagReplaceStart}}More details{{tagReplaceEnd}} to review any required actions or other information.',
          replace: { chargeDate }
        }
      )
        .replace('{{tagReplaceStart}}', '<strong>')
        .replace('{{tagReplaceEnd}}', '</strong>');

      return (
        <SubscriptionWarning
          data={{
            type: 'informative' as NotificationType,
            label: translate('subscriptions.moreDetails', 'More details'),
            subscriptionType: subscription?.type,
            subscriptionState: SubscriptionsWarningState.EXPIRING,
            title: (
              <span
                data-testid="subscription-status-expiring"
                dangerouslySetInnerHTML={{ __html: htmlString }}
              ></span>
            )
          }}
        />
      );
    }

    function renderSubscriptionWarningError() {
      return (
        <SubscriptionWarning
          data={{
            type: 'negative' as NotificationType,
            label: translate('subscriptions.fixNow', 'Fix now'),
            subscriptionType: subscription?.type,
            subscriptionState: SubscriptionsWarningState.ERROR,
            title: (
              <span data-testid="subscription-status-error">
                {`${translate(
                  'subscriptions.warningInactiveMessageError',
                  'Attention needed: There’s an issue with your subscription affecting service continuity. Please address promptly.'
                )}`}
              </span>
            )
          }}
        />
      );
    }

    function renderSubscriptionWarningInactive() {
      const htmlString = translate(
        'subscriptions.warningInactiveMessageInactive',
        'Your subscription has expired. Click {{tagReplaceStart}}More details {{tagReplaceEnd}}to review additional information and explore additional HP services.'
      );
      const [start, middle, end] = htmlString.split(
        /{{\/?tagReplaceStart}}|{{\/?tagReplaceEnd}}/
      );

      return (
        <SubscriptionWarning
          data={{
            type: 'informative' as NotificationType,
            label: translate('subscriptions.moreDetails', 'More details'),
            subscriptionType: subscription?.type,
            subscriptionState: SubscriptionsWarningState.INACTIVE,
            title: (
              <span data-testid="subscription-status-inactive">
                {start}
                <strong>{middle}</strong>
                {end}
              </span>
            )
          }}
        />
      );
    }

    switch (warningState) {
      case SubscriptionsWarningState.PENDING:
        return renderSubscriptionWarningPending();
      case SubscriptionsWarningState.ACTIVE:
        return;
      case SubscriptionsWarningState.EXPIRING:
        return renderSubscriptionWarningExpiring();
      case SubscriptionsWarningState.ERROR:
        return renderSubscriptionWarningError();
      case SubscriptionsWarningState.INACTIVE:
        return renderSubscriptionWarningInactive();
      default:
        return;
    }
  }, [
    subscription?.billing?.nextBillingCycle?.chargeDate,
    subscription?.state,
    subscription?.type,
    subscriptionName,
    translate
  ]);

  const goBack = useCallback(
    () => navigation.push(`/${country}/${language}/subscriptions`),
    [country, language, navigation]
  );

  useEffect(() => {
    // Note: When the user tries to access a wrong or not existant resource ID in InstantInk,
    // it redirects since it does not find any subscription. However, for HP All In, the API is returning this
    // specific error ("Cannot read properties of null (reading 'entities')"), so it's not redirecting.
    if (
      !subscription &&
      !loading &&
      // Remove this condition when the error is fixed in the API
      (!error ||
        error?.message ===
          "Cannot read properties of null (reading 'entities')")
    ) {
      navigation.push(`/${country}/${language}/subscriptions`);
    }
  }, [country, error, language, loading, navigation, subscription]);

  useEffect(() => {
    if (subscription?.resourceId && type && !loading && !error) {
      publishEvent(generateAnalyticsOfScreenDisplayedByType(type));
    }
  }, [type, subscription, loading, error]);

  return (
    <>
      <DrillDownHeader>
        <ArrowIcon
          data-testid={`navigate-button-${isRTL ? 'right' : 'left'}`}
          onClick={() => goBack()}
          tabIndex={0}
          aria-label={`${translate('common.goBack', 'Go back')}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              goBack();
            }
          }}
        />
        <h1 className="subtitle-regular">
          {`${translate('subscriptions.subscriptionDetails', 'Subscription details')}`}
        </h1>
      </DrillDownHeader>
      {showSubscriptionWarning()}
      <Content data-testid="subscription-details">
        {loading && (
          <Section
            border="outlined"
            background="filled"
          >
            <Loading />
          </Section>
        )}
        {error && (
          <ErrorBoundary
            analyticsEvent={getModuleDisplayedSubscriptionsDetailsError(
              error?.message
            )}
            showOutline={true}
            isRounded={true}
            loading={isRefetching}
            errorInfo={{
              error,
              context: 'SubscriptionDetails',
              operation
            }}
            onRetry={handleRefetch}
          />
        )}
        {!loading && !error && subscription?.resourceId && (
          <>
            <PlanDetails
              data-testid="content"
              subscription={subscription}
            />
            <Payment subscription={subscription} />
            <Shipping subscription={subscription} />
            <HaveQuestions subscription={subscription} />
            <MoreOptions subscription={subscription} />
          </>
        )}
      </Content>
      {showOriginalFeedbackExperience && (
        <LookingForSomethingElse
          analytics={{
            screenName: screenNameSubscriptionDetails,
            screenPath: screenPathSubscriptionDetails
          }}
        />
      )}
    </>
  );
};

export default SubscriptionDetails;
